import { AuthShell, Button } from '@profylr/ui'
import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import darkIcon from '@/assets/dark_icon_transparent_background.svg'
import { CUBE_CLOSED_PHASE, ResilienceIntelligenceOrb } from '@/assets/ResilienceIntelligenceOrb'
import { detectEnvironment, getEnvironmentConfig } from '@/config/environmentBranding'
import { useAuth } from '@/hooks/useAuth'
import { useOrbAnimationCycle } from '@/hooks/useOrbAnimationCycle'
import { useAuth0 } from '@/hooks/useAuth0'
import api from '@/services/api'

export default function LoginPage() {
  const { user } = useAuth()
  const {
    isAuthenticated: isAuth0Authenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
  } = useAuth0()
  const location = useLocation()

  /*
   * Drives exactly one cycle. Left uncontrolled the orb runs its RAF loop
   * forever, which is a spinning logo behind a form somebody is trying to
   * read; passing a cycleToken makes it play the mount intro and park, the
   * same single cycle a hover buys in the sidebar.
   */
  const { cycleToken: orbCycleToken } = useOrbAnimationCycle()

  const env = getEnvironmentConfig()
  const isProduction = detectEnvironment() === 'production'

  /*
   * Warms the Auth0 config endpoint, and nothing more. The result is discarded
   * either way -- the screen is the same whether it answers or not -- so the
   * sign-in button must NOT wait on it: when the backend is slow or down this
   * request never settles, and gating `disabled` on it left the only control
   * on the page permanently unclickable with no error to explain why. Auth0's
   * own `isLoading` is the one signal worth blocking on.
   */
  useEffect(() => {
    api.get('/auth/config').catch(() => {
      // Config check failed - still show the login screen.
    })
  }, [])

  // Redirect if already authenticated
  if (user || isAuth0Authenticated) {
    const from = location.state?.from?.pathname || '/'
    return <Navigate to={from} replace />
  }

  return (
    <AuthShell
      product="Operational Resilience"
      /*
       * Runs once on mount and then parks -- the same single cycle a hover
       * buys in the sidebar. It is not left looping: nothing should keep
       * moving behind a form someone is trying to read.
       *
       * Starting on the shut cube rather than the open one turns that run into
       * an entrance. From phase 0 the first thing the page does is swallow its
       * own logo, spin it and hand it back, which reads as a loading spinner
       * on a screen that is not loading anything; from the closed pose the
       * mark arrives assembled, spins once and blooms open, and the last frame
       * of the run is the pose it parks on.
       */
      brandMark={
        <ResilienceIntelligenceOrb
          size={550}
          cycleToken={orbCycleToken}
          startPhase={CUBE_CLOSED_PHASE}
          showButton={false}
          // The cube still opens; the light behind it no longer ramps, which
          // is what read as a fade on a screen this size.
          dim={false}
        />
      }
      // The same slot as the header's: the environment, outside production.
      identity={isProduction ? undefined : <span>{env.shortLabel}</span>}
      description="Sign in to access your resilience dashboard."
      footnote="You will be redirected to our secure authentication page"
      poweredBy={
        <a
          href="https://profylr.co.uk"
          target="_blank"
          rel="noreferrer noopener"
          className="focus-ring inline-flex items-center gap-2 rounded-md px-2 py-1 outline-none transition-colors duration-fast hover:text-text-nav"
        >
          {/*
            Masked rather than drawn, so the mark takes the link's own colour
            instead of its own. An <img> cannot inherit currentColor; rendering
            the SVG as a mask over a currentColor fill means the logo matches
            the text beside it exactly, and follows it on hover.
          */}
          <span
            aria-hidden="true"
            className="h-5 w-5 shrink-0 bg-current"
            style={{
              // Quoted: React silently drops the declaration if the value is
              // not a valid CSS <image>, which leaves a filled square behind.
              maskImage: `url("${darkIcon}")`,
              WebkitMaskImage: `url("${darkIcon}")`,
              maskSize: 'contain',
              WebkitMaskSize: 'contain',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskPosition: 'center',
              WebkitMaskPosition: 'center',
            }}
          />
          <span>Powered by Profylr</span>
        </a>
      }
    >
      <Button
        type="button"
        size="lg"
        onClick={() => loginWithRedirect()}
        disabled={auth0Loading}
        className="w-full"
      >
        {auth0Loading ? 'Redirecting to login…' : 'Sign in'}
      </Button>
    </AuthShell>
  )
}
